import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { workOrdersApi, assetsApi, usersApi, defectsApi, maintenanceApi, taskLibraryApi, workOrderTasksApi, workOrderCostsApi, inventoryApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { usePlan } from '../hooks/usePlan';
import { useLookup } from '../hooks/useLookups';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { StatusPill } from '../components/ui/StatusPill';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AssetCombobox from '../components/assets/AssetCombobox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { SlideDrawer } from '../components/ui/slide-drawer';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import WorkOrderCostsCard from '../components/WorkOrderCostsCard';
import { ActionMenu } from '../components/ui/action-menu';
import { DensityToggle, SortableTh, useDensity, useSortState, clientSort } from '../components/ui/list-controls';
import { SignatureCanvas } from '../components/ui/signature-canvas';
import { AIChatPanel } from '../components/AIChat';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  format, 
  isToday, 
  isThisWeek, 
  isThisMonth, 
  startOfWeek, 
  endOfWeek, 
  subWeeks, 
  startOfMonth, 
  endOfMonth, 
  subMonths,
  isWithinInterval,
  parseISO,
  addMonths,
  subMonths as subMonthsFn,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  startOfDay,
  addDays,
  addWeeks,
  differenceInCalendarDays
} from 'date-fns';
import { 
  Loader2, 
  Wrench, 
  Plus,
  Search,
  CalendarIcon,
  CalendarDays,
  User,
  Package,
  CheckCircle,
  LayoutGrid,
  List,
  X,
  Eye,
  AlertTriangle,
  Link as LinkIcon,
  Play,
  Pause,
  Clock,
  PenLine,
  ChevronLeft,
  ChevronRight,
  GanttChartSquare,
  Move,
  Sparkles,
  Lock,
  Bot,
  ListChecks,
  Trash2,
  Copy,
  Pencil,
  Download,
  Check,
  UserCheck,
  GitMerge
} from 'lucide-react';
import { toast } from 'sonner';

const DATE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This Week' },
  { id: 'last_week', label: 'Last Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
];

// Dynamic StatusBadge using lookups
const StatusBadge = ({ status, statuses }) => {
  const config = statuses?.find((s) => s.value === status);
  const label = config?.name || status?.replace(/_/g, ' ') || 'Unknown';
  return <StatusPill value={status} label={label} />;
};

// Work Order Form Component
const WorkOrderForm = ({ assets, users, relatedDefect, relatedDefects = [], relatedDefectIds = [], relatedMaintenance, relatedMaintenanceIds = [], preSelectedAsset, preSelectedTemplate, prefillTitle, prefillDescription, onSuccess, onCancel }) => {
  // When a work order is created from one or more defects the asset is fixed
  // (all linked defects belong to the same asset), so the picker is locked.
  const lockedAsset = !!(relatedDefect || relatedDefectIds.length > 0);
  const lockedAssetObj = lockedAsset ? assets.find(a => a.id === (preSelectedAsset || relatedDefect?.asset_id)) : null;
  const defectList = relatedDefects.length > 0 ? relatedDefects : (relatedDefect ? [relatedDefect] : []);
  const { formatDate } = useSettings();
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    asset_id: preSelectedAsset || '',
    defect_id: relatedDefect?.id || null,
    maintenance_schedule_id: relatedMaintenance?.id || null,
    title: prefillTitle || (relatedDefect ? `Repair: ${relatedDefect.title}` : (relatedMaintenance ? `PM: ${relatedMaintenance.name}` : '')),
    description: prefillDescription || relatedDefect?.description || '',
    assigned_to: '',
  });

  // Task template suggestions
  const [suggestedTemplates, setSuggestedTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Editable task checklist (set up in the workshop office at creation time)
  const [taskLines, setTaskLines] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  // Track which library templates were applied, so the backend can bump
  // their "times used" / "last used" usage stats on submit.
  const [appliedTemplateIds, setAppliedTemplateIds] = useState([]);
  const appliedPreselect = useRef(false);

  useEffect(() => {
    taskLibraryApi.list()
      .then(res => setAllTemplates(Array.isArray(res.data) ? res.data : (res.data?.items || [])))
      .catch(() => setAllTemplates([]));
  }, []);

  const applyTemplate = (templateId) => {
    const tmpl = allTemplates.find(t => t.id === templateId);
    if (!tmpl) return;
    const lines = (tmpl.steps || []).map(s => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      description: s.description || '',
      estimated_minutes: s.estimated_minutes ?? '',
    }));
    setTaskLines(prev => [...prev, ...lines]);
    setAppliedTemplateIds(prev => prev.includes(templateId) ? prev : [...prev, templateId]);
    toast.success(`${lines.length} task${lines.length === 1 ? '' : 's'} added from "${tmpl.name}"`);
  };

  // Auto-apply a template passed via ?template= (the Task Library "Add to
  // work order" action) once the templates have loaded.
  useEffect(() => {
    if (preSelectedTemplate && allTemplates.length > 0 && !appliedPreselect.current) {
      appliedPreselect.current = true;
      applyTemplate(preSelectedTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedTemplate, allTemplates]);

  const addTaskLine = () => setTaskLines(prev => [...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, description: '', estimated_minutes: '' }]);
  const updateTaskLine = (id, field, value) => setTaskLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  const removeTaskLine = (id) => setTaskLines(prev => prev.filter(l => l.id !== id));

  // Fetch suggestions when asset changes
  useEffect(() => {
    if (formData.asset_id) {
      setLoadingSuggestions(true);
      taskLibraryApi.suggestForAsset(formData.asset_id)
        .then(res => setSuggestedTemplates(res.data || []))
        .catch(() => setSuggestedTemplates([]))
        .finally(() => setLoadingSuggestions(false));
    } else {
      setSuggestedTemplates([]);
      setSelectedTemplate(null);
    }
  }, [formData.asset_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error('Please select an asset');
      return;
    }

    setSubmitting(true);
    try {
      await workOrdersApi.create({
        ...formData,
        scheduled_date: date.toISOString(),
        assigned_to: formData.assigned_to || null,
        task_library_id: null,
        applied_template_ids: appliedTemplateIds,
        tasks: taskLines
          .filter(l => (l.description || '').trim())
          .map(l => ({
            description: l.description.trim(),
            estimated_minutes: l.estimated_minutes === '' || l.estimated_minutes == null ? null : Number(l.estimated_minutes),
          })),
        linked_defect_ids: relatedDefectIds.length > 0 ? relatedDefectIds : undefined,
        maintenance_schedule_ids: relatedMaintenanceIds.length > 0 ? relatedMaintenanceIds : undefined,
      });
      toast.success(relatedDefectIds.length > 1 ? `Work order created from ${relatedDefectIds.length} defects` : 'Work order created');
      onSuccess();
    } catch (error) {
      toast.error('Failed to create work order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {defectList.length > 0 && (
        <div className="id-card p-4" data-testid="wo-form-linked-defects">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A] mb-3">
            {defectList.length > 1 ? 'Linked Defects' : 'Linked Defect'}
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#FFF3C4] text-[#7A5B00]">{defectList.length}</span>
          </div>
          <div className="flex flex-col gap-2">
            {defectList.map(d => (
              <div key={d.id} className="flex items-center gap-3" data-testid={`wo-form-defect-${d.id}`}>
                <span className="font-mono text-xs text-[#B45309] shrink-0">{d.defect_number || d.id.slice(0, 8)}</span>
                <span className="flex-1 text-sm text-[#2A2A2A] truncate">{d.title}</span>
                {d.severity && (
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide shrink-0 ${d.severity === 'critical' || d.severity === 'high' ? 'bg-[#FDEDE8] text-[#B4432A] border-[#F3C7BB]' : d.severity === 'medium' ? 'bg-[#FFF3C4] text-[#7A5B00] border-[#F2D97A]' : 'bg-[#EAF3EA] text-[#2E7D32] border-[#CBE3CB]'}`}>{d.severity}</span>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {defectList.length > 1 ? 'All on the same asset — they resolve automatically when this work order is signed off.' : 'Resolves automatically when this work order is signed off.'}
          </p>
        </div>
      )}

      {relatedMaintenance && (
        <div className="id-card p-4 border-l-4 border-blue-500" data-testid="wo-form-related-maintenance">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A] mb-1">Preventative Maintenance{relatedMaintenanceIds.length > 1 ? ` (${relatedMaintenanceIds.length})` : ''}</p>
          <p className="font-medium text-[#2A2A2A]">{relatedMaintenanceIds.length > 1 ? `${relatedMaintenanceIds.length} services combined — checklists merged into this work order` : relatedMaintenance.name}</p>
        </div>
      )}

      <div className="id-card p-6 space-y-6">
        <div className="space-y-2">
          <Label>Asset *</Label>
          <AssetCombobox
            assets={assets}
            value={formData.asset_id || null}
            onChange={(v) => setFormData(prev => ({ ...prev, asset_id: v || '' }))}
            placeholder="Select asset"
            className="w-full"
            disabled={lockedAsset}
            testId="asset-select"
          />
          {lockedAsset && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="wo-asset-locked-note">
              <Lock className="w-3 h-3" />
              Locked to {lockedAssetObj ? (lockedAssetObj.unit_number || lockedAssetObj.name) : 'the defect asset'} — every linked defect is on this asset.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Work order title"
            required
            className="input-industrial"
            data-testid="workorder-title-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Describe the work to be done..."
            required
            rows={4}
            data-testid="workorder-description-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Scheduled Date *</Label>
          <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-left font-normal input-industrial" data-testid="date-picker">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formatDate(date)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-2">
        <Label>Assign To</Label>
        <Select 
          value={formData.assigned_to || 'unassigned'} 
          onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_to: v === 'unassigned' ? '' : v }))}
        >
          <SelectTrigger className="input-industrial" data-testid="assign-select">
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {users.filter(u => u.role === 'mechanic').map(u => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tasks checklist — set up the job in the workshop before it goes to the field */}
      <div className="space-y-3" data-testid="wo-form-tasks">
        <div className="flex items-center justify-between">
          <Label className="label-overline flex items-center gap-2">
            <ListChecks className="w-3.5 h-3.5" />
            Tasks {taskLines.length > 0 && <span className="text-[#8A857A] font-normal">({taskLines.length})</span>}
          </Label>
          <Button type="button" variant="outline" size="sm" onClick={addTaskLine} data-testid="wo-form-add-task-line">
            <Plus className="w-3.5 h-3.5 mr-1" />Add task
          </Button>
        </div>

        {allTemplates.length > 0 && (
          <Select value="" onValueChange={applyTemplate}>
            <SelectTrigger className="input-industrial" data-testid="wo-form-template-select">
              <SelectValue placeholder="Load from a task template…" />
            </SelectTrigger>
            <SelectContent>
              {allTemplates.map(t => {
                const suggested = suggestedTemplates.some(s => s.id === t.id);
                return (
                  <SelectItem key={t.id} value={t.id} data-testid={`wo-form-template-${t.id}`}>
                    {t.name} · {t.steps?.length || 0} steps{suggested ? ' · suggested' : ''}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}

        {taskLines.length === 0 ? (
          <p className="text-xs text-muted-foreground">No tasks yet. Add them manually or load a template so the checklist is ready for the field.</p>
        ) : (
          <div className="space-y-2">
            {taskLines.map((line, i) => (
              <div key={line.id} className="flex items-center gap-2" data-testid={`wo-form-task-line-${i}`}>
                <span className="text-xs text-[#8A857A] w-5 shrink-0 text-right">{i + 1}.</span>
                <Input
                  value={line.description}
                  onChange={(e) => updateTaskLine(line.id, 'description', e.target.value)}
                  placeholder="Task description"
                  className="flex-1 h-9"
                  data-testid={`wo-form-task-desc-${i}`}
                />
                <Input
                  type="number"
                  min="0"
                  value={line.estimated_minutes}
                  onChange={(e) => updateTaskLine(line.id, 'estimated_minutes', e.target.value)}
                  placeholder="min"
                  className="w-16 h-9 text-center"
                  data-testid={`wo-form-task-min-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeTaskLine(line.id)}
                  className="shrink-0 text-[#B4432A] p-1.5 hover:bg-[#FDEDE8] rounded"
                  title="Remove task"
                  data-testid={`wo-form-task-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      <div className="flex gap-4 pt-4">
        <Button type="submit" className="btn-industrial flex-1" disabled={submitting} data-testid="submit-workorder-btn">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
          {submitting ? 'Creating...' : 'Create Work Order'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-btn">
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Work Order Detail/Edit View Component
// ─── Work Order Task Checklist Component ─────────────────────────────
const WorkOrderTaskList = ({ workOrder, onRefresh }) => {
  const [tasks, setTasks] = useState(workOrder?.tasks || []);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { setTasks(workOrder?.tasks || []); }, [workOrder?.tasks]);

  if (tasks.length === 0 && workOrder.status === 'completed') return null;

  const completedCount = tasks.filter(t => t.completed).length;
  const progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  const handleToggle = async (taskId, completed) => {
    try {
      await workOrderTasksApi.updateTask(workOrder.id, taskId, { completed: !completed });
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleAddTask = async () => {
    if (!newTaskDesc.trim()) return;
    setAdding(true);
    try {
      await workOrderTasksApi.addTask(workOrder.id, { description: newTaskDesc.trim() });
      setNewTaskDesc('');
      setShowAddTask(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to add task');
    } finally { setAdding(false); }
  };

  return (
    <Card data-testid="wo-task-checklist">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm uppercase tracking-wide flex items-center gap-2">
            <ListChecks className="w-4 h-4" />
            Tasks ({completedCount}/{tasks.length})
          </CardTitle>
          {workOrder.status !== 'completed' && (
            <Button size="sm" variant="outline" onClick={() => setShowAddTask(!showAddTask)} data-testid="wo-add-task-btn">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          )}
        </div>
        {tasks.length > 0 && (
          <div className="w-full bg-secondary rounded-full h-1.5 mt-2">
            <div className="bg-[#FFD108] h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-1">
        {showAddTask && (
          <div className="flex gap-2 mb-3">
            <Input
              value={newTaskDesc}
              onChange={e => setNewTaskDesc(e.target.value)}
              placeholder="New task description..."
              className="flex-1"
              onKeyDown={e => e.key === 'Enter' && handleAddTask()}
              data-testid="wo-new-task-input"
            />
            <Button size="sm" className="btn-volt" onClick={handleAddTask} disabled={adding}>
              {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
            </Button>
          </div>
        )}
        {tasks.sort((a, b) => (a.order || 0) - (b.order || 0)).map(task => (
          <div
            key={task.id}
            className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${
              task.completed ? 'bg-green-900/10' : 'hover:bg-secondary/50'
            }`}
            data-testid={`wo-task-${task.id}`}
          >
            <button
              onClick={() => handleToggle(task.id, task.completed)}
              disabled={workOrder.status === 'completed'}
              className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                task.completed
                  ? 'bg-green-500 border-green-500'
                  : 'border-gray-600 hover:border-[#FFD108]'
              }`}
            >
              {task.completed && <Check className="w-3 h-3 text-white" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                {task.description}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                {task.estimated_minutes && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />{task.estimated_minutes}m
                  </span>
                )}
                {task.completed_by && (
                  <span className="text-xs text-green-400">{task.completed_by}</span>
                )}
                {task.source === 'ad_hoc' && (
                  <Badge className="bg-blue-500/20 text-blue-400 text-[10px] px-1 py-0">Ad-hoc</Badge>
                )}
                {task.notes && (
                  <span className="text-xs text-muted-foreground italic truncate max-w-[200px]">{task.notes}</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {tasks.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-3">No tasks assigned</p>
        )}
      </CardContent>
    </Card>
  );
};

const WorkOrderDetailView = ({ workOrder, asset, defect, linkedDefects, maintenance, users, statuses, onUpdate, onClose, onRefresh, onDelete }) => {
  const { isAdmin, isMechanic } = useAuth();
  const { formatDate, formatDateTime } = useSettings();
  const { isPro, onUpgrade } = usePlan();
  const [updating, setUpdating] = useState(false);
  const [completionModal, setCompletionModal] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [signature, setSignature] = useState('');
  const [completionNotes, setCompletionNotes] = useState(workOrder?.notes || '');
  const [elapsedTime, setElapsedTime] = useState(workOrder?.total_time_seconds || 0);
  const navigate = useNavigate();

  // Timer effect - update elapsed time when running
  useEffect(() => {
    if (workOrder?.timer_is_running && workOrder?.timer_started_at) {
      const startedAt = new Date(workOrder.timer_started_at);
      const baseTime = workOrder.total_time_seconds || 0;
      
      const interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now - startedAt) / 1000);
        setElapsedTime(baseTime + elapsed);
      }, 1000);
      
      return () => clearInterval(interval);
    } else {
      setElapsedTime(workOrder?.total_time_seconds || 0);
    }
  }, [workOrder?.timer_is_running, workOrder?.timer_started_at, workOrder?.total_time_seconds]);

  // Format seconds to HH:MM:SS
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUpdate = async (data) => {
    setUpdating(true);
    try {
      const response = await onUpdate(data);
      // Check for completion summary
      if (response?._completion_summary) {
        setCompletionModal(response._completion_summary);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleStartTimer = async () => {
    setUpdating(true);
    try {
      await workOrdersApi.startTimer(workOrder.id);
      toast.success('Timer started - Asset status changed to "In Maintenance"');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start timer');
    } finally {
      setUpdating(false);
    }
  };

  const handlePauseTimer = async () => {
    setUpdating(true);
    try {
      await workOrdersApi.pauseTimer(workOrder.id);
      toast.success('Timer paused');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause timer');
    } finally {
      setUpdating(false);
    }
  };

  const handleResumeTimer = async () => {
    setUpdating(true);
    try {
      await workOrdersApi.resumeTimer(workOrder.id);
      toast.success('Timer resumed');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume timer');
    } finally {
      setUpdating(false);
    }
  };

  const handleCompleteWithSignature = async () => {
    if (!signature) {
      toast.error('Please provide a signature');
      return;
    }
    
    setUpdating(true);
    try {
      const response = await workOrdersApi.completeWithSignature(workOrder.id, {
        signature: signature,
        notes: completionNotes
      });
      setShowSignatureModal(false);
      if (response.data?._completion_summary) {
        setCompletionModal(response.data._completion_summary);
      } else {
        toast.success('Work order completed');
      }
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete work order');
    } finally {
      setUpdating(false);
    }
  };

  const [addingEntry, setAddingEntry] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [, setTick] = useState(0);
  const [addingCost, setAddingCost] = useState(null); // 'parts' | 'misc' | null
  const [editingCostId, setEditingCostId] = useState(null);
  const [costForm, setCostForm] = useState({ description: '', quantity: '1', unit_cost: '', supplier: '', part_number: '', part_id: null });
  const [catalogueParts, setCatalogueParts] = useState([]);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [entryH, setEntryH] = useState('');
  const [entryM, setEntryM] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [savingTask, setSavingTask] = useState(false);

  const tasks = workOrder?.tasks || [];
  const tasksDone = tasks.filter(t => t.completed).length;
  const allTasksDone = tasks.length === 0 || tasksDone === tasks.length;
  const isCompleted = workOrder?.status === 'completed';
  const entries = workOrder?.time_entries || [];
  const rate = workOrder?.labour_rate || 0;
  const sched = scheduleRelation(workOrder?.scheduled_date, workOrder?.status);
  const canEdit = isAdmin() || isMechanic();

  useEffect(() => {
    if (!entries.some(e => e.is_running)) return;
    const i = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(i);
  }, [entries]);

  const effSec = (e) => (e.seconds || 0) + (e.is_running && e.started_at ? Math.max(0, Math.floor((Date.now() - new Date(e.started_at)) / 1000)) : 0);
  const totalSec = entries.reduce((sum, e) => sum + effSec(e), 0);
  const secToHM = (s) => `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  const labourCost = (totalSec / 3600) * rate;
  const money = (n) => `A$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const refresh = () => onRefresh && onRefresh();

  const doStartEntry = async () => { try { await workOrdersApi.startEntry(workOrder.id); refresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not start timer'); } };
  const doPauseEntry = async (eid) => { try { await workOrdersApi.pauseEntry(workOrder.id, eid); refresh(); } catch (e) { toast.error('Could not pause timer'); } };
  const doAddManual = async () => {
    const secs = (parseInt(manualHours || 0, 10) * 3600) + (parseInt(manualMinutes || 0, 10) * 60);
    if (!secs) { toast.error('Enter hours or minutes'); return; }
    try { await workOrdersApi.addEntry(workOrder.id, { seconds: secs }); setManualHours(''); setManualMinutes(''); setAddingEntry(false); refresh(); }
    catch (e) { toast.error('Could not add entry'); }
  };
  const doDeleteEntry = async (eid) => { try { await workOrdersApi.deleteEntry(workOrder.id, eid); refresh(); } catch (e) { toast.error('Could not remove entry'); } };
  const copyRef = () => { navigator.clipboard?.writeText(workOrder?.reference || ''); toast.success('Reference copied'); };
  const initials = (name) => (name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  const doAddTask = async () => {
    if (!newTaskDesc.trim()) { toast.error('Enter a task description'); return; }
    setSavingTask(true);
    try {
      await workOrderTasksApi.addTask(workOrder.id, { description: newTaskDesc.trim() });
      setNewTaskDesc(''); setAddingTask(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add task'); }
    finally { setSavingTask(false); }
  };
  const doToggleTask = async (taskId, completed) => {
    try { await workOrderTasksApi.updateTask(workOrder.id, taskId, { completed: !completed }); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update task'); }
  };
  const doDeleteTask = async (taskId) => {
    try { await workOrderTasksApi.deleteTask(workOrder.id, taskId); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove task'); }
  };

  const costItems = workOrder?.cost_line_items || [];
  const openCost = (cat) => {
    setEditingCostId(null);
    setCostForm({ description: '', quantity: '1', unit_cost: '', supplier: '', part_number: '', part_id: null });
    setAddingCost(cat);
    if (cat === 'parts' && catalogueParts.length === 0) {
      inventoryApi.listParts().then(r => setCatalogueParts(r.data || [])).catch(() => {});
    }
  };
  const openEditCost = (it) => { setAddingCost(it.category); setEditingCostId(it.id); setCostForm({ description: it.description || '', quantity: String(it.quantity || 1), unit_cost: String(it.unit_cost || ''), supplier: it.supplier || '', part_number: it.part_number || '', part_id: it.part_id || null }); };
  const pickCataloguePart = (pid) => {
    const p = catalogueParts.find(x => x.id === pid);
    if (!p) { setCostForm(f => ({ ...f, part_id: null })); return; }
    setCostForm(f => ({ ...f, part_id: p.id, description: p.name, part_number: p.part_number || '', unit_cost: String(p.unit_cost ?? ''), supplier: p.supplier || '' }));
  };
  const doAddCost = async () => {
    if (!costForm.description.trim() || costForm.unit_cost === '' || Number.isNaN(Number(costForm.unit_cost))) {
      toast.error('Enter a description and unit cost');
      return;
    }
    const payload = {
      description: costForm.description.trim(),
      category: addingCost,
      quantity: Number(costForm.quantity || 1),
      unit_cost: Number(costForm.unit_cost),
      supplier: costForm.supplier.trim() || null,
      part_number: costForm.part_number.trim() || null,
      part_id: costForm.part_id || null,
      depot_id: costForm.part_id ? (asset?.depot_id || null) : null,
    };
    try {
      if (editingCostId) await workOrderCostsApi.editItem(workOrder.id, editingCostId, payload);
      else { const r = await workOrderCostsApi.addItem(workOrder.id, payload); if (r.data?.stock_warning) toast.warning(r.data.stock_warning); }
      setAddingCost(null); setEditingCostId(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save item'); }
  };
  const doDeleteCost = async (itemId) => {
    try { await workOrderCostsApi.deleteItem(workOrder.id, itemId); refresh(); }
    catch (e) { toast.error('Could not remove item'); }
  };
  const openEditEntry = (e) => { setEditingEntryId(e.id); setEntryH(String(Math.floor((e.seconds || 0) / 3600))); setEntryM(String(Math.floor(((e.seconds || 0) % 3600) / 60))); };
  const doSaveEntry = async () => {
    const secs = (parseInt(entryH || 0, 10) * 3600) + (parseInt(entryM || 0, 10) * 60);
    try { await workOrdersApi.editEntry(workOrder.id, editingEntryId, { seconds: secs }); setEditingEntryId(null); refresh(); }
    catch (e) { toast.error('Could not update entry'); }
  };
  const doDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await workOrdersApi.downloadPdf(workOrder.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${workOrder.reference || 'work-order'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not generate PDF'); }
    finally { setDownloadingPdf(false); }
  };

  const signBlockers = [];
  if (!allTasksDone) signBlockers.push(`${tasks.length - tasksDone} task${tasks.length - tasksDone === 1 ? '' : 's'} outstanding`);
  if (!signature) signBlockers.push('Signature required');


  return (
    <div className="flex flex-col gap-5" data-testid="wo-detail">
      <Dialog open={!!completionModal} onOpenChange={() => setCompletionModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#2E7D32]" /> Work order completed</DialogTitle>
            <DialogDescription>The asset was returned to Active and any linked defects resolved.</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A857A]">
            Work Order
            <button onClick={copyRef} className="inline-flex items-center gap-1 text-[#2A2A2A] hover:text-[#C9A400]" data-testid="wo-ref-copy">
              {workOrder?.reference || '—'} <Copy className="w-3 h-3" />
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={doDownloadPdf} disabled={downloadingPdf} data-testid="wo-download-pdf">
            <Download className="w-3.5 h-3.5 mr-1.5" />{downloadingPdf ? 'Preparing…' : 'Download PDF'}
          </Button>
        </div>
        <h2 className="text-2xl font-bold text-[#1A1A1A] mt-1 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <TypeBadge wo={workOrder} /> {workOrder?.title}
        </h2>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <WoStatusPill status={workOrder?.status} />
          <span className="font-mono text-[#2A2A2A]">{sched.dateStr}</span>
          <span className={sched.overdue ? 'text-[#B4432A] font-medium' : 'text-muted-foreground'}>{sched.relStr}</span>
        </div>
      </div>

      {asset && (
        <div className="id-card p-4 flex items-center gap-3" data-testid="wo-asset-card">
          <div className="w-10 h-10 rounded-lg bg-[#F1EFEA] flex items-center justify-center shrink-0"><Package className="w-5 h-5 text-[#8A857A]" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Asset</div>
            <div className="font-semibold text-[#1A1A1A]">{asset.name}</div>
            <div className="text-xs font-mono text-muted-foreground">{asset.unit_number}{asset.serial_number ? ` \u00b7 ${asset.serial_number}` : ''}</div>
          </div>
          <button onClick={() => navigate(`/assets?view=${asset.id}`)} className="text-sm font-medium text-[#B45309] hover:underline shrink-0" data-testid="wo-open-asset">Open asset →</button>
        </div>
      )}

      {workOrder?.status === 'cancelled' && workOrder?.merged_into && (
        <div className="id-card p-4 flex items-start gap-2.5 border-l-4 border-l-[#8A857A]" data-testid="wo-merged-banner">
          <GitMerge className="w-4 h-4 mt-0.5 text-[#59554D] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A] mb-0.5">Merged</div>
            <p className="text-sm text-[#2A2A2A] leading-relaxed">
              This work order was combined into{' '}
              <button onClick={() => navigate(`/work-orders?view=${workOrder.merged_into}`)} className="font-semibold text-[#B45309] hover:underline" data-testid="wo-merged-link">
                {workOrder.merged_into_reference || 'the survivor work order'}
              </button>. Its tasks and costs now live there.
            </p>
          </div>
        </div>
      )}

      {workOrder?.self_allocated_by_name && (
        <div className="id-card p-4 flex items-start gap-2.5" data-testid="wo-self-allocated">
          <UserCheck className="w-4 h-4 mt-0.5 text-[#7A5B00] shrink-0" />
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A] mb-0.5">Activity</div>
            <p className="text-sm text-[#2A2A2A] leading-relaxed">
              <span className="font-semibold">{workOrder.self_allocated_by_name}</span> self-allocated this scheduled service from the mobile app
              {workOrder.self_allocated_at ? <span className="text-[#8A857A]"> · {formatDate(workOrder.self_allocated_at)}</span> : null}
            </p>
          </div>
        </div>
      )}

      {workOrder?.description && (
        <div className="id-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A] mb-1.5">Description</div>
          <p className="text-sm text-[#2A2A2A] leading-relaxed">{workOrder.description}</p>
        </div>
      )}

      {linkedDefects && linkedDefects.length > 0 && (
        <div className="id-card p-4" data-testid="wo-linked-defects">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A] mb-3">Linked Defects <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#FFF3C4] text-[#7A5B00]">{linkedDefects.length}</span></div>
          <div className="flex flex-col gap-2">
            {linkedDefects.map(d => (
              <button key={d.id} onClick={() => navigate(`/defects?view=${d.id}`)} className="flex items-center gap-3 text-left hover:bg-secondary/40 rounded px-1 py-1" data-testid={`wo-linked-defect-${d.id}`}>
                <span className="font-mono text-xs text-[#B45309]">{d.defect_number || d.id.slice(0, 8)}</span>
                <span className="flex-1 text-sm text-[#2A2A2A] truncate">{d.title}</span>
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${d.severity === 'critical' || d.severity === 'high' ? 'bg-[#FDEDE8] text-[#B4432A] border-[#F3C7BB]' : d.severity === 'medium' ? 'bg-[#FFF3C4] text-[#7A5B00] border-[#F2D97A]' : 'bg-[#EAF3EA] text-[#2E7D32] border-[#CBE3CB]'}`}>{d.severity}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">All resolve automatically on sign-off.</p>
        </div>
      )}

      <div className="id-card p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Tasks <span className="text-[#2A2A2A]">{tasksDone} / {tasks.length}</span></div>
          {canEdit && !isCompleted && (
            <Button variant="outline" size="sm" onClick={() => setAddingTask(v => !v)} data-testid="wo-add-task-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add task
            </Button>
          )}
        </div>
        {tasks.length > 0 && (<div className="h-1.5 rounded-full bg-[#EDEAE2] overflow-hidden mb-3"><div className="h-full bg-[#FFD108]" style={{ width: `${(tasksDone / tasks.length) * 100}%` }} /></div>)}
        {addingTask && (
          <div className="flex items-center gap-2 mb-3">
            <Input
              value={newTaskDesc}
              onChange={e => setNewTaskDesc(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doAddTask()}
              placeholder="New task description…"
              className="flex-1 h-9"
              autoFocus
              data-testid="wo-new-task-input"
            />
            <Button size="sm" onClick={doAddTask} disabled={savingTask} data-testid="wo-new-task-save">
              {savingTask ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
            </Button>
          </div>
        )}
        {tasks.length === 0 && !addingTask ? (
          <p className="text-sm text-muted-foreground">No tasks on this work order.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-2.5 text-sm group" data-testid={`wo-task-${t.id}`}>
                <button
                  type="button"
                  onClick={() => canEdit && !isCompleted && doToggleTask(t.id, t.completed)}
                  disabled={!canEdit || isCompleted}
                  className={`w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${t.completed ? 'bg-[#FFD108] border-[#FFD108]' : 'border-[#CFC8B8] hover:border-[#FFD108]'} ${(!canEdit || isCompleted) ? 'cursor-default' : 'cursor-pointer'}`}
                  data-testid={`wo-task-toggle-${t.id}`}
                >
                  {t.completed && <Check className="w-3 h-3 text-black" />}
                </button>
                <span className={t.completed ? 'line-through text-muted-foreground flex-1' : 'text-[#2A2A2A] flex-1'}>{t.description || t.name}</span>
                {canEdit && !isCompleted && (
                  <button
                    type="button"
                    onClick={() => doDeleteTask(t.id)}
                    className="shrink-0 text-[#B4432A] opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove task"
                    data-testid={`wo-task-delete-${t.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="id-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Time on Job <span className="text-[#2A2A2A]">{secToHM(totalSec)}</span></div>
          {canEdit && !isCompleted && <Button variant="outline" size="sm" onClick={() => setAddingEntry(v => !v)} data-testid="wo-add-entry">Add entry</Button>}
        </div>
        {addingEntry && (
          <div className="flex items-center gap-2 mb-3">
            <Input type="number" min="0" placeholder="h" value={manualHours} onChange={e => setManualHours(e.target.value)} className="w-16" data-testid="wo-entry-hours" />
            <Input type="number" min="0" placeholder="m" value={manualMinutes} onChange={e => setManualMinutes(e.target.value)} className="w-16" data-testid="wo-entry-minutes" />
            <Button size="sm" onClick={doAddManual} data-testid="wo-entry-save">Add</Button>
          </div>
        )}
        <div className="flex flex-col gap-2">
          {entries.length === 0 && <p className="text-sm text-muted-foreground">No time recorded yet.</p>}
          {entries.map(e => (
            <div key={e.id} className={`flex items-center gap-3 rounded-lg border p-2.5 ${e.is_running ? 'bg-[#FFFBEA] border-[#F2D97A]' : 'border-[#E6E2D9]'}`} data-testid={`wo-entry-${e.id}`}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${e.is_running ? 'bg-[#FFD108] text-black' : 'bg-[#EDEAE2] text-[#59554D]'}`}>{initials(e.user_name)}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[#1A1A1A] truncate">{e.user_name}</div>
                <div className="text-[11px] text-muted-foreground">{e.is_running ? `Running since ${format(new Date(e.started_at), 'HH:mm')}` : e.source === 'manual' ? 'Entered manually' : 'Timer'}</div>
              </div>
              {editingEntryId === e.id ? (
                <div className="flex items-center gap-1.5">
                  <Input type="number" min="0" value={entryH} onChange={ev => setEntryH(ev.target.value)} className="w-14 h-8" data-testid={`wo-entry-edit-h-${e.id}`} />
                  <span className="text-xs text-muted-foreground">h</span>
                  <Input type="number" min="0" max="59" value={entryM} onChange={ev => setEntryM(ev.target.value)} className="w-14 h-8" data-testid={`wo-entry-edit-m-${e.id}`} />
                  <span className="text-xs text-muted-foreground">m</span>
                  <Button size="sm" onClick={doSaveEntry} data-testid={`wo-entry-edit-save-${e.id}`}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingEntryId(null)}>×</Button>
                </div>
              ) : (
                <>
                  <span className="font-semibold text-sm text-[#1A1A1A]">{secToHM(effSec(e))}</span>
                  {canEdit && !isCompleted && (
                    e.is_running ? (
                      <button onClick={() => doPauseEntry(e.id)} className="w-8 h-8 rounded-md bg-[#FFD108] text-black flex items-center justify-center" data-testid={`wo-entry-pause-${e.id}`}><Pause className="w-4 h-4" /></button>
                    ) : (
                      <>
                        <button onClick={() => openEditEntry(e)} className="w-8 h-8 rounded-md border border-[#E6E2D9] text-muted-foreground hover:text-[#C9A400] flex items-center justify-center" data-testid={`wo-entry-edit-${e.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => doDeleteEntry(e.id)} className="w-8 h-8 rounded-md border border-[#E6E2D9] text-muted-foreground hover:text-[#B4432A] flex items-center justify-center" data-testid={`wo-entry-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                      </>
                    )
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        {canEdit && !isCompleted && (
          <button onClick={doStartEntry} className="mt-3 w-full h-10 rounded-lg border border-dashed border-[#D4CFC2] text-sm font-medium text-[#59554D] hover:bg-secondary/40 inline-flex items-center justify-center gap-2" data-testid="wo-start-my-timer"><Play className="w-4 h-4" /> Start my timer</button>
        )}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#EFEBE2] text-sm">
          <span className="text-muted-foreground">Labour at {money(rate)}/hr</span>
          <span className="font-bold text-[#1A1A1A]">{money(labourCost)}</span>
        </div>
      </div>

      <div className="id-card p-4" data-testid="wo-costs">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A]">Costs</div>
          <span className="font-bold text-[#1A1A1A]">{money(labourCost + (workOrder?.parts_cost || 0) + (workOrder?.misc_cost || 0))}</span>
        </div>
        <div className="flex flex-col gap-1.5 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Labour <span className="text-xs">{secToHM(totalSec)} across {entries.length} {entries.length === 1 ? 'person' : 'people'}</span></span><span>{money(labourCost)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Parts</span><span>{money(workOrder?.parts_cost)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Misc</span><span>{money(workOrder?.misc_cost)}</span></div>
        </div>

        {costItems.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-3 pt-3 border-t border-[#EFEBE2]">
            {costItems.map(it => (
              <div key={it.id} className="flex items-center gap-2 text-[13px]" data-testid={`wo-cost-item-${it.id}`}>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-[3px] ${it.category === 'parts' ? 'bg-[#EAF1F7] text-[#2A5F86]' : 'bg-[#F1EFEA] text-[#8A857A]'}`}>{it.category}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[#2A2A2A] truncate">{it.description}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</div>
                  {(it.supplier || it.part_number) && (
                    <div className="text-[11px] text-muted-foreground truncate">{[it.part_number, it.supplier].filter(Boolean).join(' · ')}</div>
                  )}
                </div>
                <span className="text-[#2A2A2A]">{money(it.total)}</span>
                {canEdit && !isCompleted && (
                  <>
                    <button onClick={() => openEditCost(it)} className="text-muted-foreground hover:text-[#C9A400]" data-testid={`wo-cost-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => doDeleteCost(it.id)} className="text-muted-foreground hover:text-[#B4432A]" data-testid={`wo-cost-del-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit && !isCompleted && (
          addingCost ? (
            <div className="mt-3 pt-3 border-t border-[#EFEBE2] flex flex-col gap-2" data-testid="wo-cost-form">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase text-[#8A857A] w-12">{addingCost}</span>
                <Input placeholder="Description" value={costForm.description} onChange={e => setCostForm(f => ({ ...f, description: e.target.value }))} className="flex-1" data-testid="wo-cost-desc" />
              </div>
              {addingCost === 'parts' && (
                <select value={costForm.part_id || ''} onChange={e => pickCataloguePart(e.target.value)} className="h-9 rounded-md border border-[#DAD5CA] px-2 text-sm" data-testid="wo-cost-catalogue">
                  <option value="">Free text (not from catalogue)</option>
                  {catalogueParts.map(p => <option key={p.id} value={p.id}>{p.part_number} — {p.name} (on hand {p.total_on_hand})</option>)}
                </select>
              )}
              {addingCost === 'parts' && (
                <div className="flex items-center gap-2">
                  <Input placeholder="Part number" value={costForm.part_number} onChange={e => setCostForm(f => ({ ...f, part_number: e.target.value }))} className="flex-1" data-testid="wo-cost-partno" />
                  <Input placeholder="Supplier" value={costForm.supplier} onChange={e => setCostForm(f => ({ ...f, supplier: e.target.value }))} className="flex-1" data-testid="wo-cost-supplier" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input type="number" min="1" step="1" placeholder="Qty" value={costForm.quantity} onChange={e => setCostForm(f => ({ ...f, quantity: e.target.value }))} className="w-20" data-testid="wo-cost-qty" />
                <Input type="number" min="0" step="0.01" placeholder="Unit cost" value={costForm.unit_cost} onChange={e => setCostForm(f => ({ ...f, unit_cost: e.target.value }))} className="w-28" data-testid="wo-cost-unit" />
                <Button size="sm" onClick={doAddCost} data-testid="wo-cost-save">{editingCostId ? 'Save' : 'Add'}</Button>
                <Button size="sm" variant="outline" onClick={() => { setAddingCost(null); setEditingCostId(null); }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => openCost('parts')} data-testid="wo-add-parts"><Plus className="w-3.5 h-3.5 mr-1" />Add parts</Button>
              <Button variant="outline" size="sm" onClick={() => openCost('misc')} data-testid="wo-add-misc"><Plus className="w-3.5 h-3.5 mr-1" />Add misc</Button>
            </div>
          )
        )}
      </div>

      <div className="id-card p-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A] mb-2">Assignment</div>
        <div className="text-xs text-muted-foreground mb-1">Responsible</div>
        {canEdit && !isCompleted ? (
          <Select value={workOrder?.assigned_to || 'unassigned'} onValueChange={(v) => handleUpdate({ assigned_to: v === 'unassigned' ? null : v })}>
            <SelectTrigger data-testid="wo-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {(users || []).map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <div className="text-sm text-[#2A2A2A]">{workOrder?.assigned_name || 'Unassigned'}</div>
        )}
      </div>

      {!isCompleted && (
        <div className={`rounded-xl border p-4 ${signBlockers.length ? 'border-[#F3C7BB] bg-[#FDEDE8]' : 'border-[#E6E2D9] bg-white'}`} data-testid="wo-signoff">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#B4432A]">Sign-off</div>
            {signBlockers.length > 0 && <span className="text-xs font-semibold text-[#B4432A]">{signBlockers.length} items blocking</span>}
          </div>
          <div className="flex flex-col gap-1.5 text-sm mb-3">
            <div className="flex items-start gap-2">{allTasksDone ? <Check className="w-4 h-4 text-[#2E7D32] mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-[#B4432A] mt-0.5" />}<span><b>{tasks.length === 0 ? 'No tasks to complete.' : allTasksDone ? 'All tasks complete.' : `${tasks.length - tasksDone} task(s) outstanding.`}</b> {allTasksDone ? '' : 'All tasks must be checked to sign off.'}</span></div>
            <div className="flex items-start gap-2">{signature ? <Check className="w-4 h-4 text-[#2E7D32] mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-[#B4432A] mt-0.5" />}<span><b>{signature ? 'Signature captured.' : 'Signature required.'}</b> {linkedDefects?.length ? `Sign-off resolves ${linkedDefects.length} defect${linkedDefects.length === 1 ? '' : 's'} and returns the asset to Active.` : ''}</span></div>
            <div className="flex items-start gap-2">{totalSec > 0 ? <Check className="w-4 h-4 text-[#2E7D32] mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-[#C9A400] mt-0.5" />}<span>{secToHM(totalSec)} labour recorded.{totalSec === 0 ? ' Consider adding time.' : ''}</span></div>
          </div>
          <button disabled={!allTasksDone} onClick={() => setShowSignatureModal(true)} className={`w-full h-12 rounded-lg font-semibold inline-flex items-center justify-center gap-2 transition-colors ${allTasksDone ? 'bg-[#FFD108] text-black hover:bg-[#EAC200]' : 'bg-[#EDEAE2] text-[#A8A296] cursor-not-allowed'}`} data-testid="wo-complete-signoff">
            <PenLine className="w-4 h-4" /> Complete & Sign Off
          </button>
          {signBlockers.length > 0 && <p className="text-xs text-[#B4432A] mt-2">Complete the blocking items above to enable sign-off.</p>}
        </div>
      )}

      <div className="id-card p-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A857A] mb-2">Work Notes</div>
        {workOrder?.notes && <div className="rounded-lg bg-[#F8F6F2] p-3 text-sm text-[#2A2A2A] mb-2">{workOrder.notes}</div>}
        {!isCompleted && (
          <Textarea placeholder="Add a note about the work being done..." value={completionNotes} onChange={e => setCompletionNotes(e.target.value)} data-testid="wo-notes-input" />
        )}
      </div>

      {isAdmin() && onDelete && (
        <div className="flex items-center justify-between gap-4 pt-1">
          <p className="text-xs text-muted-foreground">Deleting unlinks {linkedDefects?.length || 0} defect{(linkedDefects?.length || 0) === 1 ? '' : 's'} without removing them{totalSec > 0 ? `, and discards ${secToHM(totalSec)} of recorded labour` : ''}.</p>
          <button onClick={onDelete} className="inline-flex items-center gap-2 px-3 h-9 rounded-lg border border-[#F1C6C0] text-[#B4432A] font-semibold hover:bg-[#FEF2F0] shrink-0" data-testid="wo-delete-btn"><Trash2 className="w-4 h-4" /> Delete</button>
        </div>
      )}

      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign off work order</DialogTitle>
            <DialogDescription>Sign below to complete{linkedDefects?.length ? ` and resolve ${linkedDefects.length} linked defect${linkedDefects.length === 1 ? '' : 's'}` : ''}.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <SignatureCanvas onSave={setSignature} />
            <Button onClick={handleCompleteWithSignature} disabled={updating || !signature} data-testid="wo-signoff-confirm">Complete & Sign Off</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Calendar View Component
const CalendarView = ({ workOrders, assets, statuses, onWorkOrderClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Create asset lookup map
  const assetMap = useMemo(() => {
    const map = {};
    assets.forEach(a => { map[a.id] = a; });
    return map;
  }, [assets]);

  const getWorkOrdersForDay = (day) => {
    return workOrders.filter(wo => {
      if (!wo.scheduled_date) return false;
      const woDate = startOfDay(parseISO(wo.scheduled_date));
      return isSameDay(woDate, day);
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-green-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-400';
  };

  const getAssetName = (assetId) => {
    const asset = assetMap[assetId];
    return asset ? asset.name : 'Unknown Asset';
  };

  const prevMonth = () => setCurrentMonth(subMonthsFn(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const goToToday = () => setCurrentMonth(new Date());

  return (
    <div className="bg-card rounded-lg border">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={nextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h2 className="text-xl font-semibold ml-2">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
        </div>
        <Button variant="outline" size="sm" onClick={goToToday}>
          Today
        </Button>
      </div>

      {/* Week Day Headers */}
      <div className="grid grid-cols-7 border-b">
        {weekDays.map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground border-r last:border-r-0">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {days.map((day, idx) => {
          const dayWorkOrders = getWorkOrdersForDay(day);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isDayToday = isToday(day);
          
          return (
            <div
              key={idx}
              className={`min-h-[120px] p-1 border-r border-b last:border-r-0 ${
                !isCurrentMonth ? 'bg-muted/30' : ''
              } ${isDayToday ? 'bg-primary/5' : ''}`}
            >
              <div className={`text-sm font-medium mb-1 p-1 rounded ${
                isDayToday ? 'bg-primary text-primary-foreground w-7 h-7 flex items-center justify-center' : ''
              } ${!isCurrentMonth ? 'text-muted-foreground' : ''}`}>
                {format(day, 'd')}
              </div>
              <div className="space-y-1 overflow-y-auto max-h-[85px]">
                {dayWorkOrders.slice(0, 3).map(wo => (
                  <div
                    key={wo.id}
                    onClick={() => onWorkOrderClick(wo.id)}
                    className={`text-xs p-1.5 rounded cursor-pointer text-white hover:opacity-80 transition-opacity ${getStatusColor(wo.status)}`}
                    title={`${wo.title} - ${getAssetName(wo.asset_id)}`}
                  >
                    <div className="font-medium truncate">{wo.title}</div>
                    <div className="opacity-80 truncate flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {getAssetName(wo.asset_id)}
                    </div>
                  </div>
                ))}
                {dayWorkOrders.length > 3 && (
                  <div className="text-xs text-muted-foreground text-center">
                    +{dayWorkOrders.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 p-3 border-t text-xs">
        <span className="text-muted-foreground">Status:</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span>Open</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500"></div>
          <span>In Progress</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-gray-500"></div>
          <span>Completed</span>
        </div>
      </div>
    </div>
  );
};

// ── Work Order presentation helpers ────────────────────────────────────────
// Type is derived: anything linked to a defect is a Repair, everything else
// is Preventative maintenance. No data-model change required.
const deriveWoType = (wo) => {
  const isRepair = !!wo.defect_id || (Array.isArray(wo.linked_defect_ids) && wo.linked_defect_ids.length > 0);
  return isRepair ? { label: 'REP', title: 'Repair' } : { label: 'PM', title: 'Preventative maintenance' };
};

const woDefectCount = (wo) => {
  const arr = Array.isArray(wo.linked_defect_ids) ? wo.linked_defect_ids.length : 0;
  return arr || (wo.defect_id ? 1 : 0);
};

const TypeBadge = ({ wo }) => {
  const t = deriveWoType(wo);
  return (
    <span
      title={t.title}
      className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-[4px] bg-[#F1EFEA] text-[#8A857A] text-[10px] font-bold tracking-wide shrink-0"
      data-testid={`wo-type-${t.label.toLowerCase()}`}
    >
      {t.label}
    </span>
  );
};

// On-token status pill for the list. Overdue is surfaced on the scheduled
// column, not here, so the pill only reflects the workflow state.
const WoStatusPill = ({ status }) => {
  const map = {
    open: { label: 'Open', cls: 'bg-[#F1EFEA] text-[#6B6659] border-[#E6E2D9]' },
    in_progress: { label: 'Active', cls: 'bg-[#FFF3C4] text-[#7A5B00] border-[#F2D97A]' },
    completed: { label: 'Done', cls: 'bg-[#F1EFEA] text-[#A8A296] border-[#E6E2D9]' },
    cancelled: { label: 'Cancelled', cls: 'bg-[#FBECEC] text-[#9A3B32] border-[#EAD2CE]' },
  };
  const s = map[status] || map.open;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${s.cls}`}>
      {s.label}
    </span>
  );
};

// Returns { dateStr, relStr, overdue } describing a scheduled date's relation to today.
const scheduleRelation = (dateStr, status) => {
  if (!dateStr) return { dateStr: '—', relStr: 'No date', overdue: false };
  const d = startOfDay(parseISO(dateStr));
  const diff = differenceInCalendarDays(d, startOfDay(new Date()));
  const overdue = status !== 'completed' && diff < 0;
  let relStr;
  if (diff === 0) relStr = 'today';
  else if (diff === 1) relStr = 'tomorrow';
  else if (diff === -1) relStr = overdue ? '1 day overdue' : 'yesterday';
  else if (diff < 0) relStr = overdue ? `${-diff} days overdue` : `${-diff} days ago`;
  else relStr = `in ${diff} days`;
  return { dateStr: format(d, 'dd/MM'), relStr, overdue };
};

// ── Timeline (6-week rolling window) — replaces the month calendar ─────────
const TL_ROW_H = 44;
const TL_LABEL_W = 260;
const TL_PRESETS = {
  day: { label: 'Days', days: 7, colDays: 1, before: 1 },
  fortnight: { label: 'Fortnight', days: 14, colDays: 1, before: 3 },
  week: { label: 'Weeks', days: 42, colDays: 7, before: 14 },
};
const startForPreset = (p) => p.colDays === 7
  ? startOfWeek(addDays(new Date(), -p.before), { weekStartsOn: 0 })
  : startOfDay(addDays(new Date(), -p.before));

const TimelineView = ({ workOrders, assets, onWorkOrderClick, onReschedule, canEdit }) => {
  const [scale, setScale] = useState('week');
  const preset = TL_PRESETS[scale];
  const days = preset.days;
  const colDays = preset.colDays;
  const cols = Math.ceil(days / colDays);
  const [windowStart, setWindowStart] = useState(() => startForPreset(TL_PRESETS.week));
  const [drag, setDrag] = useState(null); // { id, dayOffset }
  const trackRef = React.useRef(null);
  const draggedRef = React.useRef(false);

  useEffect(() => { setWindowStart(startForPreset(TL_PRESETS[scale])); }, [scale]);

  const today = startOfDay(new Date());
  const windowEnd = addDays(windowStart, days - 1);
  const dayPct = 100 / days;
  const todayOffset = differenceInCalendarDays(today, windowStart);

  const assetMap = useMemo(() => { const m = {}; assets.forEach(a => { m[a.id] = a; }); return m; }, [assets]);

  const rows = useMemo(() => (
    [...workOrders]
      .filter(w => w.scheduled_date)
      .sort((a, b) => parseISO(a.scheduled_date) - parseISO(b.scheduled_date))
  ), [workOrders]);

  const backlog = rows.filter(w => w.status !== 'completed' && differenceInCalendarDays(startOfDay(parseISO(w.scheduled_date)), windowStart) < 0);
  const colStarts = Array.from({ length: cols }, (_, i) => addDays(windowStart, i * colDays));

  const statusTint = (wo) => {
    const overdue = wo.status !== 'completed' && differenceInCalendarDays(startOfDay(parseISO(wo.scheduled_date)), today) < 0;
    if (wo.status === 'completed') return 'bg-[#F4F2ED] text-[#A8A296] border-[#E6E2D9]';
    if (overdue) return 'bg-[#FDEDE8] text-[#B4432A] border-[#F3C7BB]';
    if (wo.status === 'in_progress') return 'bg-[#FFF3C4] text-[#7A5B00] border-[#F2D97A]';
    return 'bg-white text-[#59554D] border-[#E6E2D9]';
  };

  const markerOffset = (wo) => {
    const off = differenceInCalendarDays(startOfDay(parseISO(wo.scheduled_date)), windowStart);
    return Math.min(Math.max(off, 0), days - 1);
  };

  const startDrag = (e, wo) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    draggedRef.current = false;
    const onMove = (ev) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;
      draggedRef.current = true;
      const x = Math.min(Math.max(ev.clientX - rect.left, 0), rect.width);
      const dayOffset = Math.min(Math.max(Math.floor((x / rect.width) * days), 0), days - 1);
      setDrag({ id: wo.id, dayOffset });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setDrag((cur) => {
        if (cur && cur.id === wo.id) {
          const newDate = addDays(windowStart, cur.dayOffset);
          const prevDate = startOfDay(parseISO(wo.scheduled_date));
          if (differenceInCalendarDays(newDate, prevDate) !== 0) onReschedule(wo, newDate);
        }
        return null;
      });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div className="border border-[#E6E2D9] rounded-lg overflow-hidden bg-white" data-testid="wo-timeline">
      {/* Nav + legend */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E2D9]">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setWindowStart(addDays(windowStart, -colDays))} data-testid="timeline-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setWindowStart(addDays(windowStart, colDays))} data-testid="timeline-next"><ChevronRight className="w-4 h-4" /></Button>
          <span className="font-semibold ml-1 text-[15px]">{format(windowStart, 'MMM d')} – {format(windowEnd, 'MMM d yyyy')}</span>
          <Button variant="outline" size="sm" className="ml-1" onClick={() => setWindowStart(startForPreset(preset))} data-testid="timeline-today">Today</Button>
          <div className="inline-flex items-center gap-1 bg-secondary rounded-md p-0.5 ml-2">
            {Object.entries(TL_PRESETS).map(([key, p]) => (
              <button
                key={key}
                type="button"
                onClick={() => setScale(key)}
                data-testid={`timeline-scale-${key}`}
                className={`px-2.5 py-1 rounded text-[12px] font-semibold transition-colors ${scale === key ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {[['Open', 'bg-[#B8B2A6]'], ['In progress', 'bg-[#E9B400]'], ['Overdue', 'bg-[#D9502B]'], ['Done', 'bg-[#C7C1B4]']].map(([label, dot]) => (
            <span key={label} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${dot}`} />{label}</span>
          ))}
        </div>
      </div>

      {/* Out-of-window backlog banner */}
      {backlog.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-[#FFF7DA] border-b border-[#F2E4A8] text-[13px] text-[#7A5B00]" data-testid="timeline-backlog-banner">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {backlog.length} active {backlog.length === 1 ? 'work order sits' : 'work orders sit'} before this window — shown clamped at the left edge.
        </div>
      )}

      {/* Week header */}
      <div className="flex border-b border-[#E6E2D9] bg-[#FBFAF7]">
        <div style={{ width: TL_LABEL_W }} className="shrink-0 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Work Order · {rows.length}
        </div>
        <div className="flex-1 flex">
          {colStarts.map((w, i) => {
            const isTodayCol = todayOffset >= i * colDays && todayOffset < (i + 1) * colDays;
            return (
              <div key={i} style={{ width: `${100 / cols}%` }} className={`px-2 py-2 border-l border-[#E6E2D9] text-[11px] font-medium truncate ${isTodayCol ? 'bg-[#FFF7DA] text-[#7A5B00]' : 'text-[#59554D]'}`}>
                {colDays === 7
                  ? (<>{format(w, 'MMM d')} <span className="text-muted-foreground">-{format(addDays(w, 6), 'd')}</span></>)
                  : format(w, 'EEE d')}
              </div>
            );
          })}
        </div>
      </div>

      {/* Rows */}
      {rows.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground text-sm">No scheduled work orders in view.</div>
      ) : (
        <div className="flex">
          {/* Left labels */}
          <div style={{ width: TL_LABEL_W }} className="shrink-0 border-r border-[#E6E2D9]">
            {rows.map((wo) => (
              <div key={wo.id} style={{ height: TL_ROW_H }} className="flex items-center gap-2 px-4 border-b border-[#F0EDE6] overflow-hidden">
                <TypeBadge wo={wo} />
                <button type="button" onClick={() => onWorkOrderClick(wo.id)} className="truncate text-[13px] text-[#2A2A2A] hover:text-[#C9A400] hover:underline text-left" title={wo.title}>
                  {wo.title}
                </button>
              </div>
            ))}
          </div>

          {/* Timeline track */}
          <div ref={trackRef} className="relative flex-1" style={{ height: rows.length * TL_ROW_H }}>
            {/* column gridlines */}
            {colStarts.map((w, i) => (
              <div key={i} className="absolute top-0 bottom-0 border-l border-[#EFEBE2]" style={{ left: `${(i / cols) * 100}%` }} />
            ))}
            {/* today band */}
            {todayOffset >= 0 && todayOffset < days && (
              <div className="absolute top-0 bottom-0 bg-[#FFF2B8]/50 border-x border-[#F2D97A]" style={{ left: `${todayOffset * dayPct}%`, width: `${dayPct}%` }} />
            )}
            {/* row separators */}
            {rows.map((wo, i) => (
              <div key={`sep-${wo.id}`} className="absolute left-0 right-0 border-b border-[#F0EDE6]" style={{ top: (i + 1) * TL_ROW_H - 1 }} />
            ))}
            {/* markers */}
            {rows.map((wo, i) => {
              const off = drag && drag.id === wo.id ? drag.dayOffset : markerOffset(wo);
              const isBacklog = differenceInCalendarDays(startOfDay(parseISO(wo.scheduled_date)), windowStart) < 0;
              const overdue = wo.status !== 'completed' && differenceInCalendarDays(startOfDay(parseISO(wo.scheduled_date)), today) < 0;
              const asset = assetMap[wo.asset_id];
              const first = (wo.assigned_name || '').trim().split(' ')[0];
              return (
                <div key={wo.id} className="absolute" style={{ top: i * TL_ROW_H + 7, left: `${off * dayPct}%` }}>
                  <div
                    onMouseDown={(e) => startDrag(e, wo)}
                    onClick={() => { if (draggedRef.current) { draggedRef.current = false; return; } onWorkOrderClick(wo.id); }}
                    className={`group inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-md border text-[11px] font-semibold whitespace-nowrap shadow-sm select-none ${statusTint(wo)} ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${drag && drag.id === wo.id ? 'ring-2 ring-[#E9B400] z-10' : ''}`}
                    data-testid={`timeline-marker-${wo.id}`}
                    title={`${wo.title}${asset ? ' · ' + (asset.unit_number || asset.name) : ''}`}
                  >
                    {canEdit && <Move className="w-3 h-3 opacity-40 group-hover:opacity-70" />}
                    {isBacklog && <span className="text-[#B4432A]">‹</span>}
                    {overdue && <AlertTriangle className="w-3 h-3 text-[#D9502B]" />}
                    <span>{asset ? (asset.unit_number || asset.name) : 'Asset'}</span>
                    {first && <span className={overdue ? 'text-[#B4432A]/80' : 'text-muted-foreground'}>{first}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer hint */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-t border-[#E6E2D9] text-xs text-muted-foreground">
        <Move className="w-3.5 h-3.5" />
        {canEdit ? 'Drag a marker sideways to reschedule. Click the label to open the work order.' : 'Click a marker or label to open the work order.'}
      </div>
    </div>
  );
};

export const WorkOrdersListPage = () => {
  const [searchParams] = useSearchParams();
  const preSelectedAsset = searchParams.get('asset');
  const preSelectedDefect = searchParams.get('defect');
  const preSelectedDefects = searchParams.get('defects');
  const preSelectedMaintenance = searchParams.get('maintenance');
  const preSelectedMaintenanceIds = searchParams.get('maintenance_ids');
  const prefillTitle = searchParams.get('title');
  const prefillDescription = searchParams.get('description');
  const shouldOpenNew = searchParams.get('new') === 'true';
  const preSelectedTemplate = searchParams.get('template');
  const viewWorkOrderId = searchParams.get('view');
  
  const [workOrders, setWorkOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Default to hide completed
  const [dateFilter, setDateFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [page, setPage] = useState(1);
  const WO_PAGE_SIZE = 25;
  const [density, setDensity] = useDensity('workorders_density');
  const [groupBy, setGroupBy] = useState('due_next');
  const { sortKey, sortDir, toggleSort } = useSortState();
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [combineOpen, setCombineOpen] = useState(false);
  const [combinePrimary, setCombinePrimary] = useState('');
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { isAdmin, isMechanic } = useAuth();
  const toggleSelectWO = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const clearSelection = () => setSelectedIds([]);
  const toggleSelectAllWO = () => setSelectedIds((p) => {
    const ids = pagedOrders.map((w) => w.id);
    return ids.every((i) => p.includes(i)) ? p.filter((i) => !ids.includes(i)) : Array.from(new Set([...p, ...ids]));
  });
  const selectedObjs = () => workOrders.filter((w) => selectedIds.includes(w.id)).sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  const canCombine = () => {
    const objs = selectedObjs();
    return objs.length >= 2 && new Set(objs.map((o) => o.asset_id)).size === 1 && objs.every((o) => !['completed', 'cancelled'].includes(o.status));
  };
  const doBulkDelete = async () => {
    setBulkBusy(true);
    try { const r = await workOrdersApi.bulkDelete(selectedIds); toast.success(`Deleted ${r.data.deleted} work order(s)`); setBulkDeleteOpen(false); clearSelection(); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBulkBusy(false); }
  };
  const doBulkAssign = async () => {
    if (!bulkAssignTo) return;
    setBulkBusy(true);
    try { const r = await workOrdersApi.bulkAssign(selectedIds, bulkAssignTo); toast.success(`Assigned ${r.data.assigned} to ${r.data.assigned_name}`); setBulkAssignOpen(false); setBulkAssignTo(''); clearSelection(); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
    finally { setBulkBusy(false); }
  };
  const doCombine = async () => {
    setBulkBusy(true);
    try { const r = await workOrdersApi.combine(selectedIds, combinePrimary || undefined); toast.success(`Combined into ${r.data.reference}`); setCombineOpen(false); setCombinePrimary(''); clearSelection(); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Combine failed'); }
    finally { setBulkBusy(false); }
  };
  const woBulkUI = (
    <>
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#FFF9E6] border border-[#F5E3B3] rounded-lg mb-3" data-testid="wo-bulk-bar">
          <span className="text-sm font-semibold text-[#1A1A1A]">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="wo-bulk-clear">Clear</Button>
            {(isAdmin() || isMechanic()) && <Button size="sm" variant="outline" onClick={() => setBulkAssignOpen(true)} data-testid="wo-bulk-assign">Assign</Button>}
            <Button size="sm" variant="outline" disabled={!canCombine()} title={canCombine() ? '' : 'Select 2+ open work orders on the same asset'} onClick={() => { setCombinePrimary(selectedObjs()[0]?.id || ''); setCombineOpen(true); }} data-testid="wo-bulk-combine">Combine</Button>
            {isAdmin() && <Button size="sm" variant="destructive" onClick={() => setBulkDeleteOpen(true)} data-testid="wo-bulk-delete">Delete</Button>}
          </div>
        </div>
      )}
      <ConfirmDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={(o) => { if (!o) setBulkDeleteOpen(false); }}
        onConfirm={doBulkDelete}
        title="Delete work orders?"
        description={`${selectedIds.length} work order(s) will be permanently removed. Only open / assigned / in-progress can be deleted; linked defects are unlinked.`}
      />
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign {selectedIds.length} work order(s)</DialogTitle></DialogHeader>
          <Select value={bulkAssignTo} onValueChange={setBulkAssignTo}>
            <SelectTrigger data-testid="wo-bulk-assign-select"><SelectValue placeholder="Choose a person" /></SelectTrigger>
            <SelectContent>
              {(users || []).filter((u) => ['mechanic', 'fleet_manager', 'admin'].includes(u.role)).map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkAssignOpen(false)}>Cancel</Button>
            <Button disabled={!bulkAssignTo || bulkBusy} onClick={doBulkAssign} data-testid="wo-bulk-assign-confirm">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={combineOpen} onOpenChange={setCombineOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Combine {selectedIds.length} work orders</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">All tasks are preserved, grouped under each source. Pick the primary (keeps its reference); the others are cancelled and linked to it.</p>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {selectedObjs().map((w) => (
              <label key={w.id} className="flex items-center gap-2 p-2 rounded border border-[#E2DED4] cursor-pointer">
                <input type="radio" name="wo-primary" checked={combinePrimary === w.id} onChange={() => setCombinePrimary(w.id)} data-testid={`wo-combine-primary-${w.id}`} />
                <span className="text-sm font-medium">{w.reference || w.title}</span>
                <span className="text-xs text-muted-foreground">· {w.status}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCombineOpen(false)}>Cancel</Button>
            <Button disabled={bulkBusy} onClick={doCombine} data-testid="wo-combine-confirm">Combine</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [selectedLinkedDefects, setSelectedLinkedDefects] = useState([]);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [relatedDefect, setRelatedDefect] = useState(null);
  const [relatedDefects, setRelatedDefects] = useState([]);
  const [relatedDefectIds, setRelatedDefectIds] = useState([]);
  const [relatedMaintenance, setRelatedMaintenance] = useState(null);
  const [relatedMaintenanceIds, setRelatedMaintenanceIds] = useState([]);
  const [loadingWorkOrder, setLoadingWorkOrder] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Dynamic lookups
  const { data: statuses } = useLookup('work_order_status');
  
  const { formatDate } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  // Auto-open add drawer if new param is present
  useEffect(() => {
    if (shouldOpenNew && assets.length > 0 && !loading) {
      if (preSelectedDefects) {
        loadRelatedDefects(preSelectedDefects.split(',').filter(Boolean));
      } else if (preSelectedDefect) {
        loadRelatedDefect(preSelectedDefect);
      } else if (preSelectedMaintenanceIds) {
        loadRelatedMaintenances(preSelectedMaintenanceIds.split(',').filter(Boolean));
      } else if (preSelectedMaintenance) {
        loadRelatedMaintenance(preSelectedMaintenance);
      } else {
        setDrawerMode('add');
        setDrawerOpen(true);
      }
    }
  }, [shouldOpenNew, preSelectedDefect, preSelectedMaintenance, preSelectedMaintenanceIds, assets, loading]);

  // Auto-open view drawer if view param is present
  useEffect(() => {
    if (viewWorkOrderId && !loading) {
      openViewDrawer(viewWorkOrderId);
    }
  }, [viewWorkOrderId, loading]);

  const loadData = async () => {
    try {
      const [woRes, assetsRes, usersRes] = await Promise.all([
        workOrdersApi.getAll(),
        assetsApi.getAll(),
        usersApi.getAll().catch(() => ({ data: [] })),
      ]);
      setWorkOrders(woRes.data);
      setAssets(assetsRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedDefect = async (defectId) => {
    try {
      const defectRes = await defectsApi.getById(defectId);
      setRelatedDefect(defectRes.data);
      setRelatedDefects([defectRes.data]);
      setDrawerMode('add');
      setDrawerOpen(true);
    } catch (error) {
      console.error('Failed to load related defect');
    }
  };

  const loadRelatedDefects = async (ids) => {
    try {
      const results = await Promise.all(ids.map(id => defectsApi.getById(id).then(r => r.data).catch(() => null)));
      const defects = results.filter(Boolean);
      setRelatedDefect(defects[0] || null);
      setRelatedDefects(defects);
      setRelatedDefectIds(ids);
      setDrawerMode('add');
      setDrawerOpen(true);
    } catch (error) {
      console.error('Failed to load related defects');
    }
  };

  const loadRelatedMaintenance = async (maintenanceId) => {
    try {
      const res = await maintenanceApi.getById(maintenanceId);
      setRelatedMaintenance(res.data);
    } catch (error) {
      console.error('Failed to load related maintenance');
    } finally {
      setDrawerMode('add');
      setDrawerOpen(true);
    }
  };

  const loadRelatedMaintenances = async (ids) => {
    try {
      const results = await Promise.all(ids.map(id => maintenanceApi.getById(id).then(r => r.data).catch(() => null)));
      const matched = results.filter(Boolean);
      if (matched.length) {
        setRelatedMaintenance(matched[0]);
        setRelatedMaintenanceIds(ids);
      }
    } catch (error) {
      console.error('Failed to load related maintenances');
    } finally {
      setDrawerMode('add');
      setDrawerOpen(true);
    }
  };

  const loadWorkOrderDetails = async (id) => {
    setLoadingWorkOrder(true);
    try {
      const woRes = await workOrdersApi.getById(id);
      setSelectedWorkOrder(woRes.data);
      
      const asset = assets.find(a => a.id === woRes.data.asset_id);
      setSelectedAsset(asset || null);
      
      // Load primary defect
      if (woRes.data.defect_id) {
        try {
          const defectRes = await defectsApi.getById(woRes.data.defect_id);
          setSelectedDefect(defectRes.data);
        } catch (e) {
          setSelectedDefect(null);
        }
      } else {
        setSelectedDefect(null);
      }

      // Load linked defects (include the primary defect_id, de-duplicated)
      const allDefectIds = [...new Set([...(woRes.data.linked_defect_ids || []), ...(woRes.data.defect_id ? [woRes.data.defect_id] : [])])];
      if (allDefectIds.length > 0) {
        const linkedDefectPromises = allDefectIds.map(id =>
          defectsApi.getById(id).then(res => res.data).catch(() => null)
        );
        const linkedDefects = (await Promise.all(linkedDefectPromises)).filter(Boolean);
        setSelectedLinkedDefects(linkedDefects);
      } else {
        setSelectedLinkedDefects([]);
      }

      // Load maintenance schedule
      if (woRes.data.maintenance_schedule_id) {
        try {
          const maintenanceRes = await maintenanceApi.getAll();
          const maintenance = maintenanceRes.data.find(m => m.id === woRes.data.maintenance_schedule_id);
          setSelectedMaintenance(maintenance || null);
        } catch (e) {
          setSelectedMaintenance(null);
        }
      } else {
        setSelectedMaintenance(null);
      }
    } catch (error) {
      toast.error('Failed to load work order details');
      closeDrawer();
    } finally {
      setLoadingWorkOrder(false);
    }
  };

  const filterByDate = (wo) => {
    if (dateFilter === 'all') return true;
    if (!wo.scheduled_date) return dateFilter === 'all';

    const scheduledDate = parseISO(wo.scheduled_date);
    const now = new Date();

    switch (dateFilter) {
      case 'today':
        return isToday(scheduledDate);
      case 'this_week':
        return isThisWeek(scheduledDate, { weekStartsOn: 1 });
      case 'last_week':
        const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        return isWithinInterval(scheduledDate, { start: lastWeekStart, end: lastWeekEnd });
      case 'this_month':
        return isThisMonth(scheduledDate);
      case 'last_month':
        const lastMonthStart = startOfMonth(subMonths(now, 1));
        const lastMonthEnd = endOfMonth(subMonths(now, 1));
        return isWithinInterval(scheduledDate, { start: lastMonthStart, end: lastMonthEnd });
      default:
        return true;
    }
  };

  const filteredOrders = useMemo(() => {
    return workOrders.filter(wo => {
      const matchesSearch = !search || wo.title.toLowerCase().includes(search.toLowerCase());
      // 'active' filter shows open/in-progress work orders (hides completed AND cancelled)
      const matchesStatus = statusFilter === 'all' 
        ? true 
        : statusFilter === 'active' 
          ? (wo.status !== 'completed' && wo.status !== 'cancelled')
          : wo.status === statusFilter;
      const matchesDate = filterByDate(wo);
      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [workOrders, search, statusFilter, dateFilter]);

  const assetMap = useMemo(() => { const m = {}; assets.forEach(a => { m[a.id] = a; }); return m; }, [assets]);

  const sortedOrders = useMemo(
    () => clientSort(filteredOrders, sortKey, sortDir, {
      scheduled: (w) => w.scheduled_date,
      assigned_name: (w) => w.assigned_name,
      asset: (w) => assetMap[w.asset_id]?.name || assetMap[w.asset_id]?.unit_number,
    }),
    [filteredOrders, sortKey, sortDir, assetMap]
  );

  const woTotalPages = Math.max(1, Math.ceil(sortedOrders.length / WO_PAGE_SIZE));
  const pagedOrders = useMemo(
    () => sortedOrders.slice((page - 1) * WO_PAGE_SIZE, page * WO_PAGE_SIZE),
    [sortedOrders, page]
  );
  useEffect(() => { setPage(1); }, [statusFilter, search, sortKey, sortDir]);

  const statusCounts = useMemo(() => ({
    active: workOrders.filter(w => w.status !== 'completed' && w.status !== 'cancelled').length,
    in_progress: workOrders.filter(w => w.status === 'in_progress').length,
    completed: workOrders.filter(w => w.status === 'completed').length,
    all: workOrders.length,
  }), [workOrders]);

  const overdueCount = useMemo(() => workOrders.filter(w =>
    w.status !== 'completed' && w.scheduled_date &&
    differenceInCalendarDays(startOfDay(parseISO(w.scheduled_date)), startOfDay(new Date())) < 0
  ).length, [workOrders]);

  const handleReschedule = async (wo, newDate) => {
    try {
      await workOrdersApi.update(wo.id, { scheduled_date: newDate.toISOString() });
      toast.success(`Rescheduled to ${format(newDate, 'd MMM')}`);
      loadData();
    } catch (e) {
      toast.error('Could not reschedule work order');
    }
  };


  const clearFilters = () => {
    setSearch('');
    setStatusFilter('active'); // Reset to active (hide completed)
    setDateFilter('all');
  };

  const hasActiveFilters = search !== '' || statusFilter !== 'active' || dateFilter !== 'all';

  const openAddDrawer = () => {
    setSelectedWorkOrder(null);
    setRelatedDefect(null);
    setRelatedMaintenance(null);
    setRelatedMaintenanceIds([]);
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const openViewDrawer = async (woId) => {
    setDrawerMode('view');
    setDrawerOpen(true);
    await loadWorkOrderDetails(woId);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode(null);
    setSelectedWorkOrder(null);
    setSelectedAsset(null);
    setSelectedDefect(null);
    setSelectedLinkedDefects([]);
    setSelectedMaintenance(null);
    setRelatedDefect(null);
    setRelatedDefects([]);
    setRelatedDefectIds([]);
    setRelatedMaintenance(null);
    setRelatedMaintenanceIds([]);
    // Clear URL params
    navigate('/work-orders', { replace: true });
  };

  const handleFormSuccess = () => {
    closeDrawer();
    loadData();
  };

  const handleUpdate = async (data) => {
    try {
      const response = await workOrdersApi.update(selectedWorkOrder.id, data);
      // Return the response data to handle completion modal
      const responseData = response.data;
      setSelectedWorkOrder(responseData);
      loadData();
      if (!responseData._completion_summary) {
        toast.success('Work order updated');
      }
      return responseData;
    } catch (error) {
      toast.error('Failed to update work order');
      return null;
    }
  };

  const handleDelete = async () => {
    if (!selectedWorkOrder) return;
    setDeleting(true);
    try {
      await workOrdersApi.delete(selectedWorkOrder.id);
      toast.success('Work order deleted');
      setConfirmDeleteOpen(false);
      closeDrawer();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete work order');
    } finally {
      setDeleting(false);
    }
  };

  const getDrawerTitle = () => {
    switch (drawerMode) {
      case 'add': return 'Create Work Order';
      case 'view': return selectedWorkOrder?.reference ? `Work Order ${selectedWorkOrder.reference}` : 'Work Order Details';
      default: return '';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="work-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Work Orders
          </h1>
          <p className="text-muted-foreground">
            Manage maintenance and repair tasks
            {overdueCount > 0 && <span className="text-[#B4432A] font-medium"> · {overdueCount} overdue</span>}
          </p>
        </div>
        {(isAdmin() || isMechanic()) && (
          <Button onClick={openAddDrawer} className="btn-industrial" data-testid="create-workorder-btn">
            <Plus className="w-5 h-5 mr-2" />
            Create Work Order
          </Button>
        )}
      </div>

      {/* Filters + list — single card (gold standard) */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search work orders..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="workorder-search-input"
                />
              </div>
              {/* Segmented status tabs */}
              <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1">
                {[
                  { id: 'active', label: 'Active', count: statusCounts.active },
                  { id: 'in_progress', label: 'In Progress', count: statusCounts.in_progress },
                  { id: 'completed', label: 'Completed', count: statusCounts.completed },
                  { id: 'all', label: 'All', count: statusCounts.all },
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setStatusFilter(tab.id)}
                    data-testid={`wo-tab-${tab.id}`}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${
                      statusFilter === tab.id ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span className="uppercase tracking-wide">{tab.label}</span>
                    <span className={`text-[11px] font-bold ${statusFilter === tab.id ? 'text-black/70' : 'text-muted-foreground'}`}>{tab.count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm text-muted-foreground" data-testid="wo-result-count">
                {filteredOrders.length} of {workOrders.length} work orders
              </span>
              {viewMode === 'list' && <DensityToggle density={density} onChange={setDensity} />}
              {viewMode === 'list' && (
                <div className="flex items-center gap-0.5 rounded-md bg-secondary p-0.5" data-testid="wo-groupby-toggle">
                  {[{ id: 'due_next', label: 'Due next' }, { id: 'by_asset', label: 'By asset' }].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setGroupBy(opt.id)}
                      className={`px-3 h-8 rounded-[6px] text-sm font-medium transition-colors ${groupBy === opt.id ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid={`wo-groupby-${opt.id}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-0.5 rounded-md bg-secondary p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`w-8 h-8 rounded-[6px] flex items-center justify-center transition-colors ${viewMode === 'list' ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="list-view-btn"
                  aria-label="List view"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('timeline')}
                  className={`w-8 h-8 rounded-[6px] flex items-center justify-center transition-colors ${viewMode === 'timeline' ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="timeline-view-btn"
                  aria-label="Timeline view"
                >
                  <GanttChartSquare className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="py-16 text-center">
            <Wrench className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Work Orders Found</h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first work order'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
      ) : viewMode === 'timeline' ? (
        <div className="p-4">
          <TimelineView
            workOrders={filteredOrders}
            assets={assets}
            onWorkOrderClick={openViewDrawer}
            onReschedule={handleReschedule}
            canEdit={isAdmin() || isMechanic()}
          />
        </div>
      ) : (
          <div className="overflow-x-auto">
            {woBulkUI}
            <table className="data-table w-full" data-density={density}>
                <thead>
                  <tr className="border-b border-border">
                    <th className="p-4 w-8 text-center">
                      <input type="checkbox" data-testid="wo-select-all"
                        checked={pagedOrders.length > 0 && pagedOrders.every(w => selectedIds.includes(w.id))}
                        onChange={toggleSelectAllWO} />
                    </th>
                    <SortableTh sortKey="title" label="Work Order" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="asset" label="Asset" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="scheduled" label="Scheduled" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="assigned_name" label="Assigned To" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableTh sortKey="status" label="Status" current={sortKey} dir={sortDir} onSort={toggleSort} />
                    <th className="text-right p-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(groupBy === 'by_asset'
                    ? [...sortedOrders].sort((a, b) => {
                        const an = assetMap[a.asset_id]?.name || assetMap[a.asset_id]?.unit_number || 'Unknown asset';
                        const bn = assetMap[b.asset_id]?.name || assetMap[b.asset_id]?.unit_number || 'Unknown asset';
                        return an.localeCompare(bn);
                      })
                    : pagedOrders
                  ).map((wo, idx, arr) => {
                    const rel = scheduleRelation(wo.scheduled_date, wo.status);
                    const asset = assetMap[wo.asset_id];
                    const dcount = woDefectCount(wo);
                    const assetName = asset ? (asset.name || asset.unit_number) : 'Unknown asset';
                    const showHeader = groupBy === 'by_asset' && (idx === 0 || arr[idx - 1].asset_id !== wo.asset_id);
                    return (
                    <React.Fragment key={wo.id}>
                    {showHeader && (
                      <tr className="bg-[#FBFAF7] border-b border-[#E6E2D9]" data-testid={`workorder-group-${wo.asset_id}`}>
                        <td colSpan={7} className="px-4 py-2">
                          <span className="font-semibold text-[#2A2A2A] text-[12px]">{assetName}</span>
                          <span className="text-muted-foreground text-[12px]"> · {arr.filter(x => x.asset_id === wo.asset_id).length} work orders</span>
                        </td>
                      </tr>
                    )}
                    <tr 
                      className="border-b border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                      onClick={() => openViewDrawer(wo.id)}
                      data-testid={`workorder-row-${wo.id}`}
                    >
                      <td className="p-4 w-8 text-center" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`wo-select-${wo.id}`}
                          checked={selectedIds.includes(wo.id)}
                          onChange={() => toggleSelectWO(wo.id)} />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <TypeBadge wo={wo} />
                          <span className="font-medium text-[#2A2A2A]">{wo.title}</span>
                          {dcount > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground" title={`${dcount} linked ${dcount === 1 ? 'defect' : 'defects'}`}>
                              <span className="text-[#C9A400]">◇</span>{dcount}
                            </span>
                          )}
                        </div>
                        {wo.reference && <div className="text-[11px] font-mono text-muted-foreground mt-0.5" data-testid={`workorder-ref-${wo.id}`}>{wo.reference}</div>}
                      </td>
                      <td className="p-4">
                        <div className="text-[13px] text-[#2A2A2A]">{asset ? (asset.name || asset.unit_number) : 'Unknown asset'}</div>
                        {asset?.unit_number && <div className="text-[11px] font-mono text-muted-foreground">{asset.unit_number}</div>}
                      </td>
                      <td className="p-4">
                        <div className={rel.overdue ? 'text-[#B4432A] font-medium' : 'text-[#2A2A2A]'}>{rel.dateStr}</div>
                        <div className={`text-[11px] ${rel.overdue ? 'text-[#B4432A]' : 'text-muted-foreground'}`}>{rel.relStr}</div>
                      </td>
                      <td className="p-4">
                        {wo.assigned_name || <span className="text-[#B4772A] font-medium">Unassigned</span>}
                      </td>
                      <td className="p-4">
                        <WoStatusPill status={wo.status} />
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openViewDrawer(wo.id); }}
                          data-testid={`workorder-open-${wo.id}`}
                        >
                          Open
                        </Button>
                      </td>
                    </tr>
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            {groupBy === 'due_next' && woTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="wo-pager">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {woTotalPages} · {sortedOrders.length} work orders
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="wo-page-prev">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={page === woTotalPages} onClick={() => setPage(p => Math.min(woTotalPages, p + 1))} data-testid="wo-page-next">
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
      )}
      </Card>

      {/* Slide Drawer */}
      <SlideDrawer
        open={drawerOpen}
        onOpenChange={closeDrawer}
        title={getDrawerTitle()}
        size="lg"
        className="sheet-ground"
      >
        {loadingWorkOrder ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : drawerMode === 'add' ? (
          <WorkOrderForm 
            assets={assets}
            users={users}
            relatedDefect={relatedDefect}
            relatedDefects={relatedDefects}
            relatedMaintenance={relatedMaintenance}
            relatedMaintenanceIds={relatedMaintenanceIds}
            preSelectedAsset={preSelectedAsset || relatedDefect?.asset_id || relatedMaintenance?.asset_id}
            preSelectedTemplate={preSelectedTemplate}
            relatedDefectIds={relatedDefectIds}
            prefillTitle={prefillTitle}
            prefillDescription={prefillDescription}
            onSuccess={handleFormSuccess}
            onCancel={closeDrawer}
          />
        ) : drawerMode === 'view' && selectedWorkOrder ? (
          <WorkOrderDetailView 
            workOrder={selectedWorkOrder}
            asset={selectedAsset}
            defect={selectedDefect}
            linkedDefects={selectedLinkedDefects}
            maintenance={selectedMaintenance}
            users={users}
            statuses={statuses}
            onUpdate={handleUpdate}
            onClose={closeDrawer}
            onDelete={() => setConfirmDeleteOpen(true)}
            onRefresh={async () => {
              await loadWorkOrderDetails(selectedWorkOrder.id);
              // Also refresh the work orders list when completed
              loadData();
            }}
          />
        ) : null}
      </SlideDrawer>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={(o) => { if (!o) setConfirmDeleteOpen(false); }}
        title="Delete work order?"
        description={
          selectedWorkOrder
            ? `${selectedWorkOrder.work_order_number || ''} ${selectedWorkOrder.title ? `· ${selectedWorkOrder.title}` : ''} will be permanently removed.`
            : ''
        }
        footnote="Any defects linked to this work order will be unlinked but not removed. This action cannot be undone."
        loading={deleting}
        onConfirm={handleDelete}
        testId="confirm-delete-workorder"
      />
    </div>
  );
};

// Keep these for backward compatibility - they redirect to list page
export const WorkOrderDetailPage = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/work-orders', { replace: true });
  }, [navigate]);
  return null;
};

export const WorkOrderFormPage = () => {
  const [searchParams] = useSearchParams();
  const defectId = searchParams.get('defect');
  const assetId = searchParams.get('asset');
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect with query params preserved
    const params = new URLSearchParams();
    if (defectId) params.set('defect', defectId);
    if (assetId) params.set('asset', assetId);
    const query = params.toString();
    navigate(`/work-orders${query ? `?${query}` : ''}`, { replace: true });
  }, [navigate, defectId, assetId]);
  
  return null;
};
